import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as promocodeActions from '@features/promocode/store/actions/promocode.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RestoreStateFromMFA } from '@rd/core/mfa/store/actions/mfa.actions';
import {
  MFAAutoRedemption,
  MFAOneTimeRedemption,
  MFAOutcome,
  MFAProductType,
  MFARedemptionType,
  MFAShoppingCart,
  OneTimeRedemptionCashBack,
} from '@rd/core/mfa/types';
import { HttpError } from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { CashbackApiService } from '../../services/cash-back-api.service';
import {
  HydrateCashback,
  SubmitCashback,
  SubmitCashbackFail,
  SubmitCashbackSuccess,
} from '../actions/cashback.actions';

@Injectable()
export class CashbackEffects {
  constructor(
    private actions$: Actions,
    private cashBackApiService: CashbackApiService,
    private router: Router,
  ) {}

  submitCashback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubmitCashback),
      switchMap(
        ({
          loyaltyProgramId,
          formData: { sourceAmount, targetAmount },
          promocode,
        }) => {
          return this.cashBackApiService
            .postCashRedemption(
              {
                redemptionAmount: sourceAmount,
                cashAmount: targetAmount,
                loyaltyProgramId,
              },
              promocode,
            )
            .pipe(
              map((pointTransaction) =>
                SubmitCashbackSuccess(pointTransaction),
              ),
              catchError((error: HttpError) => of(SubmitCashbackFail(error))),
            );
        },
      ),
    ),
  );

  hydrateOneTimeRedemptionCashback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RestoreStateFromMFA),
      switchMap(({ outcome, data }) => {
        // this hydrates the store so that cashback redeem page guard passes
        const finalActions = [];
        if (outcome === MFAOutcome.Success && this.isMFADataCashback(data)) {
          finalActions.push(
            HydrateCashback({
              hydrationData: data,
              outcome,
            }),
          );

          const {
            data: { promocode, promocodeUsage },
          } = data;

          // If there is any promocode state
          // then we will hydrate it
          if (promocode && promocodeUsage) {
            finalActions.push(
              promocodeActions.HydratePromocode({
                promocode: { code: promocode },
                promocodeUsage: promocodeUsage,
              }),
            );
          }
        }
        return finalActions;
      }),
    ),
  );

  initiateCashbackCheckout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HydrateCashback),
      switchMap(({ outcome, hydrationData }) => {
        const { loyaltyProgramId, redemptionAmount, cashAmount, promocode } =
          hydrationData.data;
        const finalActions = [];
        if (outcome === MFAOutcome.Success) {
          finalActions.push(
            SubmitCashback({
              loyaltyProgramId,
              formData: {
                sourceAmount: redemptionAmount,
                targetAmount: cashAmount,
              },
              promocode: promocode ? { code: promocode } : undefined,
            }),
          );
        }
        return finalActions;
      }),
    ),
  );

  redirectToCashbackRedeemPage$ = createEffect(
    () =>
      combineLatest([
        this.actions$.pipe(ofType(RestoreStateFromMFA)),
        this.actions$.pipe(ofType(HydrateCashback)),
      ]).pipe(
        filter((values) => values.every(isNotNull)),
        tap(([restoreState]) => {
          if (restoreState.outcome === MFAOutcome.Success) {
            this.router.navigateByUrl(restoreState.data.verifiedRedirectUrl);
          }
        }),
      ),
    { dispatch: false },
  );

  private isMFADataCashback(
    data: MFAAutoRedemption | MFAOneTimeRedemption | MFAShoppingCart,
  ): data is OneTimeRedemptionCashBack {
    return (
      data.type === MFARedemptionType.OneTimeRedemtion &&
      data.product === MFAProductType.Cashback
    );
  }
}
